import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Phone } from 'lucide-react';
import { XStack, YStack } from 'tamagui';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import AppDateField from '@/components/AppDateField';
import type { GuideItem, PlaybookStep } from './guideData';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppDisclosureGlyph,
  AppFieldGroup,
  AppGuideCardDescription,
  AppGuideCardIcon,
  AppGuideCardTextStack,
  AppGuideCardTitle,
  AppGuideMetaChip,
  AppGuideMetaRow,
  AppGuideMetaText,
  AppGuideStepBadge,
  AppGuideStepBadgeText,
  AppInput,
  AppText,
} from '../../../ui-foundation';
import { AppNativeCheckbox, AppPanelButton } from '../../../ui-foundation/adapters/web';
import { buildPlaybookReminderSeeds, derivePlaybookReminderTemplates } from '../../../ui-foundation/features/guides';

const STORAGE_KEY = 'adulting_playbook_checks';

function getChecked(playbookId: string): Record<string, boolean> {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return all[playbookId] || {};
  } catch {
    return {};
  }
}

function setChecked(playbookId: string, checks: Record<string, boolean>) {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    all[playbookId] = checks;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Ignore localStorage failures and keep the guide usable.
  }
}

function capitalise(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatReviewDate(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  return `Reviewed ${new Date(value).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}`;
}

export default function PlaybookDetail({ guide, onBack }: { guide: GuideItem; onBack: () => void }) {
  const { user } = useApp();
  const isPlaybook = guide.kind === 'playbook';
  const steps = isPlaybook ? (guide.steps as PlaybookStep[]) : [];
  const [expandedStep, setExpandedStep] = useState<number>(steps[0]?.step ?? 1);
  const [checks, setChecks] = useState<Record<string, boolean>>(() => (isPlaybook ? getChecked(guide.id) : {}));
  const [anchorDate, setAnchorDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [creatingReminders, setCreatingReminders] = useState(false);
  const reviewCadenceLabel =
    isPlaybook && guide.review_frequency_months === 1
      ? 'Reviewed monthly'
      : isPlaybook
        ? `Reviewed every ${guide.review_frequency_months} months`
        : null;
  const reviewedLabel = formatReviewDate(guide.last_reviewed_at);
  const followThroughReminders = useMemo(
    () => (isPlaybook ? buildPlaybookReminderSeeds(guide.title, anchorDate) : []),
    [anchorDate, guide.title, isPlaybook],
  );
  const hasFollowThrough = useMemo(
    () => (isPlaybook ? derivePlaybookReminderTemplates(guide.title).length > 0 : false),
    [guide.title, isPlaybook],
  );

  useEffect(() => {
    setAnchorDate(new Date().toISOString().slice(0, 10));
  }, [guide.id]);

  const toggleCheck = useCallback(
    (key: string) => {
      if (!isPlaybook) {
        return;
      }

      setChecks((previous) => {
        const next = { ...previous, [key]: !previous[key] };
        setChecked(guide.id, next);
        return next;
      });
    },
    [guide.id, isPlaybook],
  );

  const createFollowThrough = useCallback(async () => {
    if (!user || !isPlaybook || followThroughReminders.length === 0) {
      return;
    }

    setCreatingReminders(true);
    try {
      const { error: deleteError } = await supabase
        .from('playbook_reminders')
        .delete()
        .eq('user_id', user.id)
        .eq('playbook_id', guide.id);

      if (deleteError) {
        throw deleteError;
      }

      const { error: insertError } = await supabase.from('playbook_reminders').insert(
        followThroughReminders.map((reminder) => ({
          user_id: user.id,
          playbook_id: guide.id,
          section: reminder.section,
          source_key: reminder.sourceKey,
          title: reminder.title,
          notes: reminder.notes,
          anchor_date: anchorDate,
          due_date: reminder.dueDate,
        })),
      );

      if (insertError) {
        throw insertError;
      }

      window.dispatchEvent(new CustomEvent('adulting:data-changed'));
      toast.success('Playbook follow-through saved to Calendar.');
    } catch (error: any) {
      toast.error(error?.message || 'Could not create playbook reminders.');
    } finally {
      setCreatingReminders(false);
    }
  }, [anchorDate, followThroughReminders, guide.id, isPlaybook, user]);

  return (
    <YStack gap="$sm" className="animate-fade-in">
      <AppButton tone="ghost" size="sm" onPress={onBack} alignSelf="flex-start">
        Back to support hub
      </AppButton>

      <AppCard>
        <AppCardBody gap="$sm">
          <XStack alignItems="flex-start" gap="$sm">
            {guide.icon ? <AppGuideCardIcon>{guide.icon}</AppGuideCardIcon> : null}
            <AppGuideCardTextStack>
              <AppGuideCardTitle>{guide.title}</AppGuideCardTitle>
              <AppGuideCardDescription>
                {isPlaybook ? guide.description : guide.summary}
              </AppGuideCardDescription>
            </AppGuideCardTextStack>
          </XStack>

          <AppGuideMetaRow>
            {isPlaybook && guide.difficulty ? (
              <AppGuideMetaChip>
                <AppGuideMetaText>{capitalise(guide.difficulty)}</AppGuideMetaText>
              </AppGuideMetaChip>
            ) : null}
            {isPlaybook && guide.estimated_time ? (
              <AppGuideMetaChip>
                <AppGuideMetaText>{guide.estimated_time}</AppGuideMetaText>
              </AppGuideMetaChip>
            ) : null}
            {isPlaybook ? (
              <AppGuideMetaChip>
                <AppGuideMetaText>
                  {steps.length} step{steps.length === 1 ? '' : 's'}
                </AppGuideMetaText>
              </AppGuideMetaChip>
            ) : null}
            {reviewedLabel ? (
              <AppGuideMetaChip>
                <AppGuideMetaText>{reviewedLabel}</AppGuideMetaText>
              </AppGuideMetaChip>
            ) : null}
            {reviewCadenceLabel ? (
              <AppGuideMetaChip>
                <AppGuideMetaText>{reviewCadenceLabel}</AppGuideMetaText>
              </AppGuideMetaChip>
            ) : null}
          </AppGuideMetaRow>
        </AppCardBody>
      </AppCard>

      {isPlaybook ? (
        <YStack gap="$sm">
          {hasFollowThrough ? (
            <AppCard>
              <AppCardBody gap="$sm">
                <AppText fontSize="$sm" fontWeight="700">
                  Playbook follow-through
                </AppText>
                <AppText fontSize="$sm" color="$colorMuted">
                  Time-sensitive steps from this guide can be added to your Adulting calendar trail in one go.
                </AppText>
                <AppDateField label="Anchor date" value={anchorDate} onChange={setAnchorDate} />
                <YStack gap="$xs">
                  {followThroughReminders.map((reminder) => (
                    <AppText key={reminder.sourceKey} fontSize="$sm">
                      {new Date(`${reminder.dueDate}T12:00:00`).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })} — {reminder.title}
                    </AppText>
                  ))}
                </YStack>
                <AppButtonRow>
                  <AppButton onPress={() => void createFollowThrough()} disabled={creatingReminders}>
                    {creatingReminders ? 'Saving reminders…' : 'Create follow-through reminders'}
                  </AppButton>
                </AppButtonRow>
              </AppCardBody>
            </AppCard>
          ) : null}

          {steps.map((step) => {
            const isOpen = expandedStep === step.step;

            return (
              <AppCard key={step.step}>
                <AppPanelButton
                  type="button"
                  onClick={() => setExpandedStep(isOpen ? -1 : step.step)}
                  className="gap-3 rounded-none border-0 bg-transparent px-4 py-4 hover:bg-muted/40"
                >
                  <AppGuideStepBadge>
                    <AppGuideStepBadgeText>{step.step}</AppGuideStepBadgeText>
                  </AppGuideStepBadge>
                  <AppText flex={1} fontSize="$sm" fontWeight="700">
                    {step.title}
                  </AppText>
                  <AppDisclosureGlyph open={isOpen} />
                </AppPanelButton>

                {isOpen ? (
                  <AppCardBody pt={0} gap="$sm" className="border-t border-border">
                    <AppText fontSize="$sm" color="$colorMuted">
                      {step.description}
                    </AppText>

                    {step.actions.length > 0 ? (
                      <div className="space-y-2">
                        {step.actions.map((action, index) => {
                          const key = `${step.step}-${index}`;
                          const checked = checks[key] || false;

                          return (
                            <label key={key} className="flex items-start gap-2.5 cursor-pointer group">
                              <AppNativeCheckbox
                                checked={checked}
                                onChange={() => toggleCheck(key)}
                                className="mt-0.5"
                              />
                              <span
                                className={cn(
                                  'text-sm leading-relaxed',
                                  checked ? 'line-through text-muted-foreground' : 'text-foreground',
                                )}
                              >
                                {action}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : null}

                    {step.call_script ? (
                      <AppCard backgroundColor="$primaryTint" borderColor="$primary">
                        <AppCardBody gap="$xs">
                          <XStack alignItems="center" gap="$xs">
                            <Phone size={12} className="text-primary" />
                            <AppText fontSize="$xs" fontWeight="700" color="$primary" textTransform="uppercase">
                              What to say
                            </AppText>
                          </XStack>
                          <AppText fontSize="$sm" lineHeight="$md" color="$color">
                            &quot;{step.call_script}&quot;
                          </AppText>
                        </AppCardBody>
                      </AppCard>
                    ) : null}

                    {step.tip ? (
                      <AppCard backgroundColor="$accentTint" borderColor="$accent">
                        <AppCardBody py="$sm">
                          <AppText fontSize="$sm">Tip: {step.tip}</AppText>
                        </AppCardBody>
                      </AppCard>
                    ) : null}

                    {step.links && step.links.length > 0 ? (
                      <AppButtonRow>
                        {step.links.map((link) => (
                          <a
                            key={`${step.step}-${link.url}`}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-2 text-sm font-medium text-primary no-underline transition-opacity hover:opacity-90"
                          >
                            {link.label}
                            <ExternalLink size={12} />
                          </a>
                        ))}
                      </AppButtonRow>
                    ) : null}
                  </AppCardBody>
                ) : null}
              </AppCard>
            );
          })}
        </YStack>
      ) : (
        <AppCard>
          <AppCardBody gap="$sm">
            <AppText fontSize="$sm" color="$colorMuted">
              {guide.summary}
            </AppText>
            <AppText fontSize="$sm">{guide.body}</AppText>
            {guide.detail ? <AppText fontSize="$sm">{guide.detail}</AppText> : null}
            {guide.link_url && guide.link_label ? (
              <a
                href={guide.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary no-underline transition-opacity hover:opacity-90"
              >
                {guide.link_label}
                <ExternalLink size={12} />
              </a>
            ) : null}
          </AppCardBody>
        </AppCard>
      )}
    </YStack>
  );
}
